import { Stack, Typography, useTheme } from "@mui/material";
import { imageSource, Strings } from "@app/constants";
import { ProductSpecificationsProps } from "../types";
import { useEffect, useState } from "react";
import { Ratings, Review } from "@app/components";
import { useDispatch, useSelector } from "react-redux";
import { getAvgReviews, getReviews } from "../slice";
import { useRequestState } from "@app/hooks";
import {
  selectAddReviewState,
  selectAvgReviewsState,
  selectGetReviewsState,
} from "../selector";

const ProductSpecifications = ({
  data,
  productId,
}: ProductSpecificationsProps) => {
  const [activeTab, setActiveTab] = useState<"specs" | "reviews">("specs");
  const theme = useTheme();
  const dispatch = useDispatch();

  const [allReviews, setAllReviews] = useState<any[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<any>({
    page: 1,
    limit: 5,
    totalPages: 1,
    total: 0,
  });
  const addReviewData = useSelector(selectAddReviewState);

  useEffect(() => {
    if (productId) {
      dispatch(getReviews({ carId: productId, page: 1, limit: 5 }));
      dispatch(getAvgReviews({ carId: productId }));
    }
  }, [productId, addReviewData]);

  const { data: reviewData } = useRequestState({
    stateSelector: selectGetReviewsState,
  });

  const { data: avgRatingData } = useRequestState({
    stateSelector: selectAvgReviewsState,
  });

  useEffect(() => {
    if (!reviewData?.data) return;

    setAllReviews((prev) => {
      const newPage = Number(reviewData.pagination?.page ?? 1);

      if (newPage === 1) {
        return reviewData.data;
      } else {
        const prevIds = new Set(prev.map((r) => r.id));
        const newOnes = reviewData.data.filter((r: any) => !prevIds.has(r.id));
        return [...prev, ...newOnes];
      }
    });

    setPaginationInfo({
      page: Number(reviewData.pagination?.page ?? 1),
      limit: Number(reviewData.pagination?.limit ?? 5),
      totalPages: Number(reviewData.pagination?.totalPages ?? 1),
      total: Number(reviewData.pagination?.total ?? 0),
    });
  }, [reviewData]);

  const handleLoadMore = () => {
    if (paginationInfo.page < paginationInfo.totalPages) {
      const nextPage = paginationInfo.page + 1;
      dispatch(
        getReviews({
          carId: productId,
          page: nextPage,
          limit: Number(paginationInfo.limit),
        })
      );
    }
  };

  const handleTabClick = (tab: "specs" | "reviews") => {
    setActiveTab(tab);
  };

  const handleRatingDistribution = () => {
    return Object.entries(avgRatingData?.ratingBreakdown || {})
      .map(([key, count]) => ({
        rating: Number(key),
        count: Number(count),
        percentage:
          avgRatingData?.totalReviews > 0
            ? Math.round((Number(count) / avgRatingData.totalReviews) * 100)
            : 0,
      }))
      .sort((a, b) => b.rating - a.rating);
  };

  return (
    <Stack px={{ xs: 1, md: 8 }} py={2} direction="column" gap={4} width="100%">
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          width: "fit-content",
        }}
      >
        <Stack
          onClick={() => handleTabClick("specs")}
          sx={{
            cursor: "pointer",
            position: "relative",
            pb: 1,
          }}
        >
          <Typography
            fontWeight="600"
            sx={{
              fontFamily: "Work Sans",
              fontSize: 20,
              color:
                activeTab === "specs"
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
            }}
          >
            {Strings.productDetail.specification}
          </Typography>
          {activeTab === "specs" && (
            <Stack
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: 3,
                bgcolor: theme.palette.primary.main,
                borderRadius: 2,
              }}
            />
          )}
        </Stack>
        <Stack
          onClick={() => handleTabClick("reviews")}
          sx={{
            cursor: "pointer",
            position: "relative",
            pb: 1,
          }}
        >
          <Typography
            fontWeight="600"
            sx={{
              fontFamily: "Work Sans",
              fontSize: 20,
              color:
                activeTab === "reviews"
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
            }}
          >
            {`Reviews(${paginationInfo?.total ?? 0})`}
          </Typography>
          {activeTab === "reviews" && (
            <Stack
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: 3,
                bgcolor: theme.palette.primary.main,
                borderRadius: 2,
              }}
            />
          )}
        </Stack>
      </Stack>
      <Stack spacing={3}>
        {activeTab === "specs" ? (
          (data?.extraInformation?.length ?? 0) > 0 ? (
            data.extraInformation.map((item: any, index: number) => (
              <Stack key={item.id || index} spacing={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Stack
                    component="img"
                    src={imageSource.checkImage}
                    alt="check"
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography fontWeight="600" fontSize={16}>
                    {item.heading}
                  </Typography>
                </Stack>
                <Stack sx={{ pl: 3, width: "100%" }}>
                  <Typography
                    sx={{
                      color: theme.palette.grey[600],
                      fontSize: 15,
                      lineHeight: 1.6,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Stack>
              </Stack>
            ))
          ) : (
            <Typography sx={{ color: theme.palette.grey[500], fontSize: 15 }}>
              No extra information available.
            </Typography>
          )
        ) : (
          <Ratings
            reviews={
              allReviews?.map((review: any) => ({
                id: review.id,
                userName: review.reviewer?.name || "Anonymous",
                date: new Date(review.createdAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }),
                rating: review.rating,
                comment: review.comment,
              })) ?? []
            }
            averageRating={avgRatingData?.avgRating}
            totalReviews={paginationInfo?.total ?? 0}
            hasMore={paginationInfo.page < paginationInfo.totalPages}
            onLoadMore={handleLoadMore}
            ratingDistribution={handleRatingDistribution()}
            productId={productId}
          />
        )}
      </Stack>
    </Stack>
  );
};

export { ProductSpecifications };
